import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FramePlaceholderBoundary } from './FramePlaceholderBoundary';
import { OrderLineItem } from './OrderLineItem';
import { PlaceholderField } from '../ui/PlaceholderField';
import { usePrototype } from '../../contexts/PrototypeContext';
import { siteConfig } from '../../data/siteConfig';
import { formatPrice } from '../../utils/currency';
import { buttonClasses } from '../../utils/buttonStyles';

/**
 * VISUAL PLACEHOLDER — native Website Forge CHECKOUT frame output.
 *
 * Contains NO inputs, card/payment fields, payment SDKs or gateways, and submits nothing.
 * Outlined boxes only indicate where the native checkout renders its own fields.
 * At integration, render the EXISTING native checkout frame and preserve its field
 * names/IDs, hidden inputs, validation, shipping/tax calculation, payment integration
 * and order submission. "Place order" here only moves to the confirmation preview.
 */
export function WfCheckoutFramePreview() {
  const {
    quantity,
    placeDemoOrder
  } = usePrototype();
  const navigate = useNavigate();
  const displayQty = quantity > 0 ? quantity : 1;
  const subtotal = siteConfig.product.samplePrice * displayQty;
  const handlePlace = () => {
    placeDemoOrder();
    navigate('/order-confirmation');
  };
  const steps = [{
    title: 'Contact',
    fields: ['Email']
  }, {
    title: 'Delivery',
    fields: ['Full name', 'Address', 'City', 'State', 'ZIP code']
  }];
  return <FramePlaceholderBoundary frame="WfCheckoutFramePreview" data-id="element-62" data-mp="zrgr8s5zlcej">
      <div className="grid gap-12 lg:grid-cols-[1.4fr_1fr] lg:gap-16" data-id="element-63" data-mp="zs08s7rq5ys1">
        <div className="space-y-10" data-id="element-64" data-mp="uzgmmmez1vbm">
          {steps.map(step => <section key={step.title} aria-label={step.title} className="border-t border-line pt-6" data-id="element-65" data-mp="q1x7zled3gj5">
              <h2 className="font-display text-2xl text-ink" data-id="element-66" data-mp="1nhb4hau7x2zb">{step.title}</h2>
              <div className="mt-5 grid gap-4 sm:grid-cols-2" data-id="element-67" data-mp="1iq2sq85f15iz">
                {step.fields.map(f => <PlaceholderField key={f} label={f} className={f === 'Address' || f === 'Email' || f === 'Full name' ? 'sm:col-span-2' : ''} data-id="element-68" data-mp="3cy6nh1r1zaox" />)}
              </div>
            </section>)}
          <section aria-label="Payment" className="border-t border-line pt-6" data-id="element-69" data-mp="338mzxiseasnq">
            <h2 className="font-display text-2xl text-ink" data-id="element-70" data-mp="38780xkfh1gd8">Payment</h2>
            <div className="mt-5 flex min-h-[120px] items-center justify-center rounded-[3px] border border-dashed border-line-strong bg-surface px-6 text-center" data-id="element-71" data-mp="3853q0ij5nh3g">
              <p className="text-[15px] text-ink-muted" data-id="element-72" data-mp="2ya3ctf22fats">Payment options appear in this step.</p>
            </div>
          </section>
        </div>

        <aside aria-label="Order summary" className="lg:sticky lg:top-24 lg:self-start" data-id="element-73" data-mp="2kblxo5q876nh">
          <div className="border-t border-ink pt-6" data-id="element-74" data-mp="2pagdqzwt6h3w">
            <h2 className="font-display text-2xl text-ink" data-id="element-75" data-mp="iixpdhyq5t0u">Order summary</h2>
            <div className="mt-6" data-id="element-76" data-mp="ngpj8hixjp7o">
              <OrderLineItem quantity={displayQty} compact data-id="element-77" data-mp="nfo687icssnb" />
            </div>
            <dl className="mt-6 space-y-3 border-t border-line pt-5 text-[15px]" data-id="element-78" data-mp="xk4kiwg6q1p0">
              <div className="flex justify-between" data-id="element-79" data-mp="sm2xk0fn1i89">
                <dt className="text-ink-muted" data-id="element-80" data-mp="snnw0152plbe">Subtotal</dt>
                <dd className="tabular-nums text-ink" data-id="element-81" data-mp="16dhiict3vthy">{formatPrice(subtotal)}</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-82" data-mp="11m97xdg1i237">
                <dt className="text-ink-muted" data-id="element-83" data-mp="11koe5q1jun6e">Shipping</dt>
                <dd className="text-ink" data-id="element-84" data-mp="1ga46vbkn52oq">Free</dd>
              </div>
              <div className="flex justify-between gap-4" data-id="element-85" data-mp="1bcu8v7j6ripa">
                <dt className="text-ink-muted" data-id="element-86" data-mp="1bef46zdbr5o7">Tax</dt>
                <dd className="text-ink-muted" data-id="element-87" data-mp="1pb2b9uyzrc7u">Calculated at checkout</dd>
              </div>
            </dl>
            <button type="button" onClick={handlePlace} className={`${buttonClasses('primary', true)} mt-8`} data-id="element-88" data-mp="1lbtf6jghb294">
              Place order
            </button>
            <Link to="/cart" className="mt-3 flex min-h-[44px] items-center justify-center text-[14px] text-ink underline underline-offset-4" data-id="element-89" data-mp="3n24v004lin4g">
              Return to bag
            </Link>
          </div>
        </aside>
      </div>
    </FramePlaceholderBoundary>;
}